import { Routes, Route, Navigate } from 'react-router'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { Toaster } from '@/components/ui/toaster'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import AdminLayout from '@/components/layout/AdminLayout'
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import Users from '@/pages/Users'
import UserDetail from '@/pages/UserDetail'
import Subscriptions from '@/pages/Subscriptions'
import CampaignManager from '@/pages/CampaignManager'
import PlanManagement from '@/pages/PlanManagement'
import Analytics from '@/pages/Analytics'
import AuditLogs from '@/pages/AuditLogs'
import PerplexityMetrics from '@/pages/PerplexityMetrics'
import AIProviders from '@/pages/AIProviders'
import AIPrompts from '@/pages/AIPrompts'
import AISecrets from '@/pages/AISecrets'
import AIModelPricing from '@/pages/AIModelPricing'
import AIUsage from '@/pages/AIUsage'
import AICostLogErrors from '@/pages/AICostLogErrors'
import BlogPosts from '@/pages/BlogPosts'
import BlogPostDetail from '@/pages/BlogPostDetail'
import { ADMIN_ROUTE_PATHS } from '@/lib/routes/admin-route-catalog'

// Derive nested (relative) paths from absolute ADMIN_ROUTE_PATHS
const ADMIN_NESTED_PATHS = Object.fromEntries(
  Object.entries(ADMIN_ROUTE_PATHS).map(([key, path]) => [key, path.replace(/^\//, '')])
) as Record<keyof typeof ADMIN_ROUTE_PATHS, string>

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const adminRoles = ['SUPPORT', 'ANALYST', 'ADMIN', 'SUPER_ADMIN']
  if (!adminRoles.includes(user.role)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p className="mt-2 text-muted-foreground">
            You do not have permission to access the admin dashboard.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path={ADMIN_NESTED_PATHS.users} element={<Users />} />
        <Route path={ADMIN_NESTED_PATHS.userDetail} element={<UserDetail />} />
        <Route path={ADMIN_NESTED_PATHS.subscriptions} element={<Subscriptions />} />
        <Route path={ADMIN_NESTED_PATHS.campaigns} element={<CampaignManager />} />
        <Route path={ADMIN_NESTED_PATHS.planManagement} element={<PlanManagement />} />
        <Route path={ADMIN_NESTED_PATHS.analytics} element={<Analytics />} />
        <Route path={ADMIN_NESTED_PATHS.perplexityMetrics} element={<PerplexityMetrics />} />
        <Route path={ADMIN_NESTED_PATHS.auditLogs} element={<AuditLogs />} />
        <Route path={ADMIN_NESTED_PATHS.aiProviders} element={<AIProviders />} />
        <Route path={ADMIN_NESTED_PATHS.aiPrompts} element={<AIPrompts />} />
        <Route path={ADMIN_NESTED_PATHS.aiSecrets} element={<AISecrets />} />
        <Route path={ADMIN_NESTED_PATHS.aiPricing} element={<AIModelPricing />} />
        <Route path={ADMIN_NESTED_PATHS.aiUsage} element={<AIUsage />} />
        <Route path={ADMIN_NESTED_PATHS.aiCostLogErrors} element={<AICostLogErrors />} />
        <Route path={ADMIN_NESTED_PATHS.blog} element={<BlogPosts />} />
        <Route path={ADMIN_NESTED_PATHS.blogDetail} element={<BlogPostDetail />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
        <Toaster />
      </AuthProvider>
    </ThemeProvider>
  )
}
